import { until } from '@vueuse/core'
import _ from 'lodash'
import { defineStore } from 'pinia'
import { camelizeKeys, decamelizeKeys } from 'humps'
import api from '@/utils/api'
import { getPlanTripId, PlanATripId, setPlanTripId } from './infoCollection'
import { toggleValue } from '@/utils/arrayUtils'
import { getAttributionData } from '@/utils/attribution'
import { createItineraryRequestApiPayload } from '@/utils/itineraryRequestApiTransform'
import { deserializeJsonApi } from '@/utils/jsonApiDeserializer'
import { TOURHERO_AI_ASSISTANT_NAME } from '../models/message'
import { generateTempId } from '@/utils/chatUtils'
import { PlanATripMessage, PlanATripSelectionOption, SelectedDates } from '@/types/chat/PlanATripMessage'
import { apiGetChatroomMessages } from './channels'
import { useAuthUserState } from './authUser'
import { MonthNames, PLAN_A_TRIP_CHAT_SOURCE, PLAN_A_TRIP_SOURCE } from '@/constants'
import { logErrorOnSentry } from '@/utils/errorReportingUtils'
import { useChatPusher } from '@/utils/pusher/useChatPusher'
import { trackPlanATripDateSelection } from '@/modules/InfoCollectionChat/planATripChatTracking'
import { formatDateUnit } from '@/utils/dateUtils'
import { metaTagContent } from '@/helpers/documentHelpers'
import { fetchFullMessageContent } from '@/utils/chat/fullMessageContent'

// Conditional debug logger that only logs in non-production environments
const createDebugLogger = () => {
  const isNonProduction = metaTagContent('nonProduction') === 'true'
  return {
    log: (...args: any[]) => isNonProduction && console.log(...args),
    debug: (...args: any[]) => isNonProduction && console.debug(...args),
  }
}

const debugLogger = createDebugLogger()

type PlanATripSelectRequestPayload = Pick<PlanATripSelectionOption, 'actionType' | 'targetField'> & {
  selection: string[] | SelectedDates
}

const NO_PREFERENCE_FOR_FOOD = 'No preference for food'
type AckStatus = 'sending' | 'reading' | 'thinking' | 'typing' | null

export const usePlanATripChatStore = defineStore('plan-a-trip-chat', {
  state: () => ({
    itineraryRequestId: null as string | number | null,
    itineraryRequestData: null as any | null,
    formState: _.cloneDeep(ItineraryRequestState),
    chatroomId: null as string | number | null,
    messages: [] as PlanATripMessage[],
    senderSlug: null as string | null,
    senderName: null as string | null,
    selectedRecommendedCountry: null as string | null,
    loading: false as boolean,
    ackStatus: null as AckStatus,
    isAiTesting: false as boolean,
    progress: 0 as number,
  }),
  getters: {
    messageWithId:
      state =>
      (id: number | null): PlanATripMessage | null => {
        return id ? (state.messages.find(message => message.id == id) ?? null) : null
      },

    apiPayload(state): object {
      const attributionData = getAttributionData()
      return createItineraryRequestApiPayload(state.formState, {
        flowCompleted: false,
        tracking: attributionData || {},
        source: PLAN_A_TRIP_CHAT_SOURCE,
      })
    },
    lastMessage(state): PlanATripMessage | null {
      return state.messages.at(-1) || null
    },
    isArchived(state): boolean {
      return state.itineraryRequestData?.infoCollectionChatroom?.status === 'archived'
    },

    aiMessages: function (state): PlanATripMessage[] {
      return state.messages.filter(msg => msg.senderName === TOURHERO_AI_ASSISTANT_NAME)
    },

    userMessages: function (state): PlanATripMessage[] {
      return state.messages.filter(msg => msg.senderName === state.senderName)
    },

    multiAiResponseMessages: function (state): PlanATripMessage[] {
      return state.messages.filter(msg => msg.senderName !== state.senderName)?.length > 0
        ? this.aiMessages.filter(
            message => message.aiResponseTo == this.userMessages[this.userMessages.length - 1]?.tempId,
          )
        : []
    },

    ackText(state): string {
      switch (state.ackStatus) {
        case 'sending': // Observationally, "reading" is almost immediately replaced by "thinking", so show same message for both for now
        case 'reading':
          return 'Maya is reading your message'
        case 'thinking':
          return 'Maya is thinking'
        case 'typing':
          return 'Maya is typing'
        default:
          return state.loading ? 'Working on it' : ''
      }
    },
  },
  actions: {
    async initialize(isAiTesting: boolean = false) {
      this.isAiTesting = isAiTesting

      debugLogger.log('[PlanATripChat] initialize', {
        isAiTesting,
        nonProduction: metaTagContent('nonProduction'),
      })

      await this.loadMessages()
      const authUser = useAuthUserState()

      const { binder } = useChatPusher()

      const bindings = {
        message_created: this.onMessageReceived,
        message_error: this.onErrorReceived,
        ai_info_collection_thinking: this.onThinkingReceived,
        ai_info_collection_typing: this.onTypingReceived,
        itinerary_request_updated: this.onItineraryRequestUpdated,
      }
      binder.bind({ slug: this.senderSlug }, [this.chatroomId], bindings)

      until(authUser.slug)
        .toBeTruthy({ timeout: 10000 })
        .then(() => {
          this.senderSlug = authUser.slug.value ?? null
          this.senderName = authUser.name.value ?? null
        })
    },
    addNewMessage(payload: PlanATripMessage | ConstructorParameters<typeof PlanATripMessage>[0]) {
      const message = payload instanceof PlanATripMessage ? payload : new PlanATripMessage({ ...payload })
      this.messages = this.messages.map(m => (m.isSame(message) ? message : m))

      if (!this.messages.find(m => m.isSame(message))) {
        const messageList = [...this.messages, message]
        this.messages = _.sortBy(_.uniqBy(messageList, 'id'), (item: PlanATripMessage) => item.sortKey())
      }
      return this.messages.find(m => m.isSame(message))
    },
    onMessageReceived: async function (payload: { message: PlanATripMessage; progress: number }) {
      const newMessage = { ...camelizeKeys(payload.message), isTyping: true }
      let restore = false
      if (newMessage.contentTruncated) {
        const local = this.messages.find(m => m.tempId && m.tempId === newMessage.tempId)
        if (local?.content) newMessage.content = local.content
        else restore = true
      }

      this.addNewMessage(newMessage)
      if (restore) this.restoreTruncatedContent(newMessage.id, newMessage.content)
      this.formState.dates = ItineraryRequestState.dates
      this.loading = false
      this.ackStatus = null

      if (payload.progress) {
        this.progress = payload.progress
      }
      await this.loadItineraryRequestData()
    },
    restoreTruncatedContent: async function (messageId: string | number, truncatedContent: string) {
      const chatroomId = this.chatroomId
      if (chatroomId == null) return

      const content = await fetchFullMessageContent(chatroomId, messageId)
      if (content === null) return

      const matched = this.messages.find(m => m.id == messageId)
      if (matched && matched.content === truncatedContent) matched.content = content
    },
    onErrorReceived(payload: any) {
      this.loading = false
      this.ackStatus = null
      this.loadItineraryRequestData()
      console.error(payload)
    },
    onThinkingReceived() {
      this.updateAIStatusMessage('thinking')
    },
    onTypingReceived() {
      this.updateAIStatusMessage('typing')
    },
    onItineraryRequestUpdated: async function () {
      await this.loadItineraryRequestData()
    },
    async createItineraryRequestWithChatroom() {
      const payload = this.apiPayload
      const { data } = await api.post(`/api/hero/itinerary_requests`, payload)

      const deserializedData = await deserializeJsonApi(data)

      this.itineraryRequestId = Number(deserializedData.id)
      setPlanTripId(this.itineraryRequestId, PlanATripId.PlanATripChat)

      this.chatroomId = deserializedData?.infoCollectionChatroom?.id
        ? deserializedData?.infoCollectionChatroom?.id
        : null

      if (deserializedData?.infoCollectionChatroom) {
        this.addNewMessage({ ...deserializedData?.infoCollectionChatroom.lastMessage, isTyping: false })
      }
    },
    async loadRecommendedCountriesData(messageId: number) {
      const response = await api.get(
        `/api/chatrooms/${this.chatroomId}/ai_info_chat/country_details?message_id=${messageId}`,
      )
      return await deserializeJsonApi(response.data)
    },
    loadItineraryRequestData: async function () {
      const itineraryRequestResponse = await api.get(
        `/api/hero/itinerary_requests/${getPlanTripId(PlanATripId.PlanATripChat)}`,
      )
      const deserializedItineraryRequest = await deserializeJsonApi(itineraryRequestResponse.data)

      if (deserializedItineraryRequest?.id && !this.itineraryRequestId) {
        this.itineraryRequestId = deserializedItineraryRequest.id
        setPlanTripId(this.itineraryRequestId, PlanATripId.PlanATripChat)
      }

      this.itineraryRequestData = deserializedItineraryRequest
      return deserializedItineraryRequest
    },

    loadMessages: async function () {
      const deserializedItineraryRequest = await this.loadItineraryRequestData()

      if (this.itineraryRequestData.source === PLAN_A_TRIP_SOURCE) {
        const searchParams = new URLSearchParams(location.search)
        window.location.replace(`/en/plan-a-trip-legacy?${searchParams.toString()}`)
      }

      const chatroomId = deserializedItineraryRequest?.infoCollectionChatroom?.id
      if (!chatroomId) {
        return
      }
      this.chatroomId = chatroomId

      const messages = await apiGetChatroomMessages(this.chatroomId)
      // flush out old messages
      this.messages = []
      messages.map(m => this.addNewMessage(m))

      this.progress = deserializedItineraryRequest?.progress ?? 0
      debugLogger.log('[PlanATripChat] Loaded messages', {
        chatroomId: this.chatroomId,
        messageCount: this.messages.length,
        progress: this.progress,
      })
    },
    sendMessage: async function (text: string) {
      if (!text) return

      const message = new PlanATripMessage({
        content: text,
        tempId: generateTempId(this.lastMessage?.id || 0),
        senderId: this.senderSlug,
        senderName: this.senderName,
      } as any)

      this.addNewMessage({ ...message, isTyping: false })

      try {
        this.loading = true
        this.updateAIStatusMessage('sending')

        const aiMessagePath = this.isAiTesting
          ? `/api/admin/chatrooms/${this.chatroomId}/ai_info_chat/message`
          : `/api/chatrooms/${this.chatroomId}/ai_info_chat/message`

        await api.post(aiMessagePath, {
          temp_id: message.id,
          content: message.content,
          sender_name: message.senderName,
        })
        this.updateAIStatusMessage('reading')
      } catch (error) {
        this.onErrorReceived(error)
      }
    },
    select: async function ({ componentInfo }: { componentInfo?: Record<string, unknown> } = {}) {
      const lastMessage = this.lastMessage
      if (!lastMessage || lastMessage.options?.length === 0) {
        return
      }

      const targetState = getFieldMap(lastMessage.options[0].targetField, this.formState)
      if (!targetState) {
        return
      }

      let actionPayloads: PlanATripSelectionOption[] = []
      if (this.selectedRecommendedCountry) {
        actionPayloads = [
          lastMessage.options.find(
            option => option.selection === this.selectedRecommendedCountry,
          ) as PlanATripSelectionOption,
        ]
      } else if (Array.isArray(targetState)) {
        actionPayloads = _.compact(
          targetState.map(purpose => {
            return lastMessage.options.find(option => option.selection === purpose)
          }),
        )
      } else if (typeof targetState === 'string') {
        const selectedOption = lastMessage.options.find(option => option.selection === targetState)
        actionPayloads = [selectedOption as PlanATripSelectionOption]
      }

      const budgetOption = actionPayloads.find(option => option?.targetField === 'budget')
      const optimisticBudget = budgetOption?.text || budgetOption?.selection || ''
      this.applyOptimisticBudgetUpdate(optimisticBudget)

      const message = new PlanATripMessage({
        tempId: generateTempId(lastMessage.id),
        content: `Selected ${actionPayloads.map(option => option.text).join(', ')}`,
        senderName: this.senderName,
        senderId: this.senderSlug,
      } as any)

      this.addNewMessage({ ...message, isTyping: false })

      this.clearForm()

      try {
        this.loading = true
        this.updateAIStatusMessage('sending')

        const aiMessagePath = this.isAiTesting
          ? `/api/admin/chatrooms/${this.chatroomId}/ai_info_chat/select`
          : `/api/chatrooms/${this.chatroomId}/ai_info_chat/select`

        await api.post(aiMessagePath, {
          temp_id: message.id,
          content: `Selected ${actionPayloads.map(option => option.text).join(', ')}`,
          sender_name: this.senderName,
          action_payload: decamelizeKeys({
            actionType: actionPayloads[0].actionType,
            targetField: actionPayloads[0].targetField,
            selection: actionPayloads.map(option => option.selection),
          } satisfies PlanATripSelectRequestPayload),
        })
        this.updateAIStatusMessage('reading')
      } catch (error) {
        this.onErrorReceived(error)
        logErrorOnSentry(error, componentInfo || { chain: 'planATripChatStore.select' })
      }
    },
    applyOptimisticBudgetUpdate(budget: string) {
      if (!budget) {
        return
      }

      const currentData = this.itineraryRequestData || {}
      this.itineraryRequestData = {
        ...currentData,
        budget,
      }
    },
    clearForm: function () {
      this.formState.group = ''
      this.formState.qualifying = ''
      this.formState.groupTypes = []
      this.formState.groupTypeOther = ''
      this.formState.purposes = []
      this.formState.accommodations = []
      this.formState.foodExperiences = []
      this.formState.destCountries = []
      this.formState.budget = ''
      this.formState.dates.duration = 0
      this.formState.dates.durationUnit = 'day'
      this.formState.dates.months = []
      this.formState.dates.customDuration = ''
      this.selectedRecommendedCountry = null
    },
    getFormattedSelectedDates() {
      const flexibleDates = _.groupBy(this.formState.dates.months, 'year')
      return Object.values(flexibleDates)
        .flat()
        .map(option => MonthNames[option.month].short + ' ' + option.year)
        .join(', ')
    },
    selectDates: async function ({ componentInfo }: { componentInfo?: Record<string, unknown> } = {}) {
      const lastMessage = this.lastMessage
      if (!lastMessage) {
        return
      }

      const monthsSnapshot = this.formState.dates.months.map(month => ({ ...month }))
      const durationValue = this.formState.dates.duration
      const durationUnitValue = this.formState.dates.durationUnit
      const dateTypeValue = this.formState.dates.type

      if (monthsSnapshot.length > 0 && durationValue > 0) {
        trackPlanATripDateSelection({
          messageId: lastMessage.id,
          months: monthsSnapshot,
          duration: durationValue,
          durationUnit: durationUnitValue,
          dateType: dateTypeValue,
          itineraryRequestId: this.itineraryRequestId,
        })
      }

      const selectedDates = this.getFormattedSelectedDates()
      const duration = formatDateUnit(this.formState.dates.duration, this.formState.dates.durationUnit)
      const content = `Selected ${selectedDates} for ${duration}`

      const message = new PlanATripMessage({
        tempId: generateTempId(lastMessage.id),
        content: content,
        senderName: this.senderName || '',
        senderId: this.senderSlug || '',
      } as any)

      this.addNewMessage({ ...message, isTyping: false })

      try {
        this.loading = true
        this.updateAIStatusMessage('sending')

        const aiMessagePath = this.isAiTesting
          ? `/api/admin/chatrooms/${this.chatroomId}/ai_info_chat/select`
          : `/api/chatrooms/${this.chatroomId}/ai_info_chat/select`

        await api.post(aiMessagePath, {
          content,
          temp_id: message.id,
          sender_name: this.senderName,
          action_payload: decamelizeKeys({
            selection: {
              dateType: this.formState.dates.type,
              duration: this.formState.dates.duration,
              durationUnit: this.formState.dates.durationUnit,
              flexibleDates: _.mapValues(
                _.groupBy(this.formState.dates.months, m => String(m.year)),
                entries => entries.map(entry => String(entry.month)),
              ),
            },
            actionType: lastMessage.options[0].actionType,
            targetField: lastMessage.options[0].targetField,
          } satisfies PlanATripSelectRequestPayload),
        })
        this.updateAIStatusMessage('reading')
      } catch (error) {
        this.onErrorReceived(error)
        logErrorOnSentry(error, componentInfo)
      }

      this.clearForm()
    },
    selectCustomDates: async function () {
      const selectedDates = this.getFormattedSelectedDates()
      const customDuration = this.formState.dates.customDuration
      const numDays = Number(customDuration)
      const durationText = Number.isFinite(numDays) ? `${numDays} day${numDays === 1 ? '' : 's'}` : customDuration
      const content = `Selected ${selectedDates} for ${durationText}`

      this.sendMessage(content)
      this.clearForm()
    },
    togglePurpose(purpose: string, force: boolean | null = null): boolean {
      this.formState.purposes = toggleValue(this.formState.purposes, purpose, force)
      return this.formState.purposes.includes(purpose)
    },
    singleSelectGroupSize(group: string) {
      this.formState.group = this.formState.group === group ? '' : group
    },
    multipleSelectPurpose(purpose: string) {
      this.formState.purposes = toggleValue(this.formState.purposes, purpose)
    },
    multipleSelectGroupType(groupType: string) {
      this.formState.groupTypes = toggleValue(this.formState.groupTypes, groupType)
    },
    multipleSelectAccommodation(accommodation: string) {
      this.formState.accommodations = toggleValue(this.formState.accommodations, accommodation)
    },
    multipleSelectFoodExperience(foodExperience: string) {
      if (foodExperience === NO_PREFERENCE_FOR_FOOD) {
        this.formState.foodExperiences = [foodExperience]
        return
      }

      if (
        this.formState.foodExperiences.includes(NO_PREFERENCE_FOR_FOOD) &&
        foodExperience !== NO_PREFERENCE_FOR_FOOD
      ) {
        this.formState.foodExperiences = [foodExperience]
        return
      }

      this.formState.foodExperiences = toggleValue(this.formState.foodExperiences, foodExperience)
    },
    multipleSelectCountry(country: string) {
      this.formState.destCountries = toggleValue(this.formState.destCountries, country)
    },
    singleSelectBudget(budget: string) {
      this.formState.budget = this.formState.budget === budget ? '' : budget
    },
    singleSelectQualifying(qualifying: string) {
      this.formState.qualifying = this.formState.qualifying === qualifying ? '' : qualifying
    },
    singleSelectRecommendCountry(country: string) {
      this.selectedRecommendedCountry = this.selectedRecommendedCountry === country ? '' : country
    },
    resetItineraryRequestId() {
      this.itineraryRequestId = null
      setPlanTripId(null, PlanATripId.PlanATripChat)
    },
    isTyping(messageId: number): boolean {
      return this.messages.find(message => message.id === messageId)?.isTyping || false
    },
    updateAIStatusMessage(newStatus: AckStatus) {
      switch (newStatus) {
        case 'sending':
          this.ackStatus = 'sending'
          break
        case 'reading':
          if (this.ackStatus === 'sending') {
            this.ackStatus = 'reading'
          }
          break
        case 'thinking':
        case 'typing':
          if (this.loading) {
            this.ackStatus = newStatus
          }
          break
      }
    },
  },
})

export const ItineraryRequestState = {
  purposes: [] as string[],
  groupTypes: [] as string[],
  groupTypeOther: '',
  group: '',
  qualifying: '' as string,
  dates: {
    type: 'flexible',
    specific: { startDate: null, endDate: null },
    duration: 0,
    durationUnit: 'day',
    months: [] as { year: number; month: number }[],
    customDuration: '',
  },
  budget: '' as string,
  accommodations: [] as string[],
  foodExperiences: [] as string[],
  destCountries: [] as string[],

  // TODO: Remove unused fields below and ensure createItineraryRequestApiPayload type still works
  tripProfile: '',
  vibeSummary: '',
  otherVibe: '',
  inspiration: [] as string[],
  country: '',
  budgetArr: null as any[] | null,
  userId: null as string | null,
  travelPace: null as string | null,
  destType: '',
  vibes: [] as string[],
}

const getFieldMap = (targetField: string, itineraryRequestState: typeof ItineraryRequestState) => {
  switch (targetField) {
    case 'group':
      return itineraryRequestState.group
    case 'qualifying':
      return itineraryRequestState.qualifying
    case 'group_type':
      return itineraryRequestState.groupTypes
    case 'purposes':
      return itineraryRequestState.purposes
    case 'accommodation':
      return itineraryRequestState.accommodations
    case 'dietary_preferences':
      return itineraryRequestState.foodExperiences
    case 'budget':
      return itineraryRequestState.budget
    case 'country':
      return itineraryRequestState.destCountries
    case 'duration_and_dates':
      return itineraryRequestState.dates
    default:
      return null
  }
}
